// src/pages/ArtistDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Music, Plus, Trash2, Calendar, Edit2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GigModal from '../components/modals/GigModal';
import AddSongModal, { upsertToSongBank } from '../components/AddSongModal';

import {
  db,
  getMasterPlaylist,
  getGigPlaylists,
  saveMasterPlaylist,
  saveGigPlaylists,
  listenToPlaylists,
  createLiveGig,
  getUserLocation,
  getActiveLiveGigForArtist,
  signOutUser,
  getArtistProfile,
  saveArtistProfile,
  saveFollowerCounts,
  listenHireRequestsForArtist,
  artistRespondToRequest,
  listenBookingsForArtist,
  listenFollowedArtists,
  publishArtistProfile,
  notifyFollowers,
  getGigAnalytics,
  getEarningsData,
  getBlockedDates,
  saveBlockedDates,
  setProfileDisabled,
} from '../firebase-utils';

import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  getDocs,
  orderBy,
} from 'firebase/firestore';

import { endLiveGig as firebaseEndLiveGig } from '../firebase-utils';

// ─── Extract YouTube ID ───────────────────────────────────────
const extractYouTubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const p of patterns) { const m = url.match(p); if (m) return m[1]; }
  return null;
};

const norm = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]/g, '');

const findBankEntry = (song, bank) => {
  if (!song || !bank?.length) return null;
  const tA = norm(song.title);
  const aA = norm(song.artist);
  return bank.find(e => {
    const tB = norm(e.title);
    const aB = norm(e.artist);
    return (tA === tB || tA.includes(tB) || tB.includes(tA)) &&
           (aA === aB || aA.includes(aB) || aB.includes(aA));
  }) || null;
};

// ============================================
// SOCIAL FOLLOW BUTTONS
// ============================================
const SocialFollowButtons = ({ socialMedia, artistName }) => {
  if (!socialMedia || !Object.values(socialMedia).some(v => v)) return null;
  const getSocialUrl = (platform, value) => {
    if (!value) return null;
    if (value.startsWith('http://') || value.startsWith('https://')) return value;
    switch (platform) {
      case 'instagram': return `https://instagram.com/${value.replace('@', '')}`;
      case 'facebook': return value.includes('facebook.com') ? value : `https://facebook.com/${value}`;
      case 'twitter': return `https://twitter.com/${value.replace('@', '')}`;
      case 'youtube': return value.includes('youtube.com') ? value : `https://youtube.com/${value}`;
      case 'spotify': return value.includes('spotify.com') ? value : `https://open.spotify.com/artist/${value}`;
      case 'tiktok': return `https://tiktok.com/@${value.replace('@', '')}`;
      case 'soundcloud': return `https://soundcloud.com/${value}`;
      default: return value;
    }
  };
  const platforms = [
    { key: 'instagram', label: '📸 Instagram', color: '#E1306C' },
    { key: 'facebook', label: '👍 Facebook', color: '#1877F2' },
    { key: 'youtube', label: '▶️ YouTube', color: '#FF0000' },
    { key: 'spotify', label: '🎵 Spotify', color: '#1DB954' },
    { key: 'twitter', label: '🐦 Twitter', color: '#1DA1F2' },
    { key: 'tiktok', label: '🎵 TikTok', color: '#000000' },
    { key: 'soundcloud', label: '☁️ SoundCloud', color: '#FF5500' },
  ];
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {platforms.map(({ key, label, color }) => {
        const value = socialMedia[key];
        if (!value) return null;
        const url = getSocialUrl(key, value);
        if (!url) return null;
        return (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer"
            className="social-btn text-xs" style={{ borderColor: color, color }}>{label}</a>
        );
      })}
    </div>
  );
};

// ============================================
// CALCULATE GIG STATUS
// ============================================
const calculateGigStatus = (gig) => {
  const now = new Date();
  const dateStr = gig.gigDate || gig.date || '';
  const timeStr = gig.gigTime || gig.time || '00:00';
  if (!dateStr) return 'upcoming';
  const gigTime = new Date(`${dateStr}T${timeStr}`);
  const endTime = new Date(gigTime.getTime() + (gig.duration || 180) * 60000);
  if (gig.status === 'cancelled') return 'cancelled';
  if (gig.status === 'ended') return 'ended';
  if (now >= gigTime && now <= endTime) return 'live';
  if (now > endTime) return 'ended';
  return 'upcoming';
};

// ============================================
// BACKING TRACK INLINE EDITOR
// ============================================
function BackingTrackEditor({ song, bankEntry, onSaved }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const ytId = extractYouTubeId(url);
  const hasYt = !!ytId;
  const existingId = bankEntry?.youtubeId || null;

  const handleSave = async () => {
    if (!ytId) return;
    setSaving(true);
    try {
      await upsertToSongBank(song.title, song.artist, ytId);
      setSaved(true);
      setUrl('');
      setTimeout(() => { setSaved(false); setOpen(false); onSaved && onSaved(); }, 1500);
    } catch (e) { alert('Error saving: ' + e.message); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <div className="flex items-center gap-1.5 mt-1">
        {existingId ? (
          <>
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(124,58,237,0.25)', color: '#c4b5fd', border: '1px solid rgba(124,58,237,0.4)' }}>
              🎵 Track linked
            </span>
            <button onClick={() => setOpen(true)}
              className="text-[10px] px-2 py-0.5 rounded font-bold transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', color: '#888', border: '1px solid rgba(255,255,255,0.1)' }}>
              ✏️ Change
            </button>
          </>
        ) : (
          <button onClick={() => setOpen(true)}
            className="text-[10px] px-2.5 py-1 rounded-lg font-bold transition-all"
            style={{ background: 'linear-gradient(135deg,rgba(245,158,11,0.2),rgba(249,115,22,0.2))', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.4)' }}>
            + Add Backing Track
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-xl p-3 space-y-2"
      style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)' }}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold" style={{ color: '#a855f7' }}>🎵 Backing Track</span>
        <button onClick={() => { setOpen(false); setUrl(''); }} className="text-gray-500 hover:text-gray-300 text-sm">✕</button>
      </div>
      <p className="text-gray-400 text-[11px]">
        Search <span className="text-purple-300">"{song.title} karaoke"</span> on YouTube → paste link below.
      </p>
      <input autoFocus value={url} onChange={e => setUrl(e.target.value)}
        placeholder="Paste YouTube URL or video ID..."
        className="w-full rounded-lg px-3 py-2 text-xs text-white"
        style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${hasYt ? 'rgba(168,85,247,0.6)' : url ? 'rgba(249,115,22,0.4)' : 'rgba(124,58,237,0.3)'}`, outline: 'none' }} />
      {hasYt && <p className="text-purple-300 text-[11px] font-semibold">✓ Valid YouTube link</p>}
      {url && !hasYt && <p className="text-orange-400 text-[11px]">⚠ Not a valid YouTube URL</p>}
      {hasYt && (
        <div className="rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
          <iframe width="100%" height="100%" src={`https://www.youtube.com/embed/${ytId}`}
            allow="encrypted-media" allowFullScreen style={{ border: 'none', display: 'block' }} />
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <button onClick={() => { setOpen(false); setUrl(''); }}
          className="flex-1 py-1.5 rounded-lg text-xs font-bold text-gray-400"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>Cancel</button>
        <button onClick={handleSave} disabled={!hasYt || saving}
          className="flex-1 py-1.5 rounded-lg text-xs font-black transition-all"
          style={{ background: saved ? '#22c55e' : !hasYt || saving ? '#444' : 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', opacity: !hasYt && !saving ? 0.5 : 1 }}>
          {saved ? '✓ Saved!' : saving ? 'Saving...' : 'Save Track'}
        </button>
      </div>
    </div>
  );
}

// ============================================
// ANALYTICS STAT CARD
// ============================================
function StatCard({ emoji, label, value, sub, color = '#00d4ff' }) {
  return (
    <div className="rounded-xl p-4 text-center flex flex-col items-center gap-1"
      style={{ background: `${color}10`, border: `1px solid ${color}30` }}>
      <span className="text-2xl">{emoji}</span>
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-xs font-bold" style={{ color }}>{label}</p>
      {sub && <p className="text-xs text-gray-500">{sub}</p>}
    </div>
  );
}

// ============================================
// MAIN ARTIST DASHBOARD COMPONENT
// ============================================
export default function ArtistDashboard({ onModeChange, onGoLive }) {
  const { currentUser } = useAuth();

  const [tab, setTab] = useState('master');
  const [masterSongs, setMasterSongs] = useState([]);
  const [gigPlaylists, setGigPlaylists] = useState([]);
  const [masterSortBy, setMasterSortBy] = useState('default');
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [showPlaylistModal, setShowPlaylistModal] = useState(false);
  const [setlistSearch, setSetlistSearch] = useState('');
  const [playlistsLoaded, setPlaylistsLoaded] = useState(false);
  const isSyncingRef = useRef(false);

  const [songBank, setSongBank] = useState([]);
  const [bankRefresh, setBankRefresh] = useState(0);
  const [addSongModal, setAddSongModal] = useState(null);
  const [itunesSearch, setItunesSearch] = useState('');
  const [itunesResults, setItunesResults] = useState([]);
  const [searchingItunes, setSearchingItunes] = useState(false);

  const [gigs, setGigs] = useState([]);
  const [editingGig, setEditingGig] = useState(null);
  const [savingGig, setSavingGig] = useState(false);
  const [capturingGPS, setCapturingGPS] = useState(false);
  const [showGigModal, setShowGigModal] = useState(false);

  const [artistProfile, setArtistProfile] = useState(null);
  const [profileData, setProfileData] = useState({
    artistName: '', fullName: '', bio: '', profilePhoto: '',
    genre: '', location: '',
    priceMin: '', priceMax: '', availabilityNote: '', isAvailable: true,
    socialMedia: { instagram: '', facebook: '', youtube: '', spotify: '', twitter: '', tiktok: '', soundcloud: '' }
  });
  const [showProfileSetup, setShowProfileSetup] = useState(false);

  const [hireRequests, setHireRequests] = useState([]);
  const [artistBookings, setArtistBookings] = useState([]);
  const [respondingTo, setRespondingTo] = useState(null);
  const [counterForm, setCounterForm] = useState({ budget: '', message: '' });
  const [expandedHireReq, setExpandedHireReq] = useState(null);

  const [gigwaveFollowerCount, setGigwaveFollowerCount] = useState(0);

  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [followerModalMode, setFollowerModalMode] = useState('reminder');
  const [followerCounts, setFollowerCounts] = useState({
    instagram: '', youtube: '', spotify: '', facebook: '', tiktok: '', soundcloud: '', twitter: ''
  });
  const [savingFollowers, setSavingFollowers] = useState(false);

  // ── Analytics state ──
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [expandedGigId, setExpandedGigId] = useState(null);

  // ── Earnings state ──
  const [earnings, setEarnings] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(false);

  // ── Profile visibility state ──
  const [togglingProfile, setTogglingProfile] = useState(false);

  // ── Availability calendar state ──
  const [blockedDates, setBlockedDates] = useState([]);
  const [availCalDate, setAvailCalDate] = useState(new Date());
  const [savingBlocked, setSavingBlocked] = useState(false);
  const [availLoaded, setAvailLoaded] = useState(false);

  // ─── Song bank live listener ──────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'backingTracks'));
    const unsub = onSnapshot(q, snap => {
      setSongBank(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => console.warn('Song bank listener:', err));
    return () => unsub();
  }, []);

  // ─── Artist profile ───────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    getArtistProfile(currentUser.uid).then(profile => {
      if (profile) {
        setArtistProfile(profile);
        setProfileData(profile);
        if (profile.followerCounts) {
          setFollowerCounts({
            instagram: profile.followerCounts.instagram || '',
            youtube: profile.followerCounts.youtube || '',
            spotify: profile.followerCounts.spotify || '',
            facebook: profile.followerCounts.facebook || '',
            tiktok: profile.followerCounts.tiktok || '',
            soundcloud: profile.followerCounts.soundcloud || '',
            twitter: profile.followerCounts.twitter || '',
          });
        }
        checkFollowerReminder(profile);
      }
    });
  }, [currentUser]);

  // ─── Load playlists ───────────────────────────────────────
  useEffect(() => {
    if (!currentUser) return;
    const load = async () => {
      try {
        const firebaseMaster = await getMasterPlaylist(currentUser.uid);
        const firebasePlaylists = await getGigPlaylists(currentUser.uid);
        const localMaster = localStorage.getItem(`GigWave_songs_${currentUser.uid}`);
        const localPlaylists = localStorage.getItem(`GigWave_playlists_${currentUser.uid}`);
        let finalMaster = firebaseMaster, finalPlaylists = firebasePlaylists, needsMigration = false;
        if (firebaseMaster.length === 0 && localMaster) {
          const p = JSON.parse(localMaster);
          if (p.length > 0) { finalMaster = p; needsMigration = true; }
        }
        if (firebasePlaylists.length === 0 && localPlaylists) {
          const p = JSON.parse(localPlaylists);
          if (p.length > 0) { finalPlaylists = p; needsMigration = true; }
        }
        setMasterSongs(finalMaster);
        setGigPlaylists(finalPlaylists);
        if (needsMigration) {
          if (finalMaster.length > 0) await saveMasterPlaylist(currentUser.uid, finalMaster);
          if (finalPlaylists.length > 0) await saveGigPlaylists(currentUser.uid, finalPlaylists);
          alert(`✅ Playlists migrated!\n${finalMaster.length} songs\n${finalPlaylists.length} playlists`);
        }
        setPlaylistsLoaded(true);
      } catch (err) { console.error(err); setPlaylistsLoaded(true); }
    };
    load();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unsub = listenToPlaylists(currentUser.uid, (data) => {
      isSyncingRef.current = true;
      setMasterSongs(data.masterSongs);
      setGigPlaylists(data.gigPlaylists);
      setTimeout(() => { isSyncingRef.current = false; }, 3000);
    });
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !playlistsLoaded || isSyncingRef.current) return;
    const id = setTimeout(() => saveMasterPlaylist(currentUser.uid, masterSongs).catch(console.error), 2000);
    return () => clearTimeout(id);
  }, [masterSongs, currentUser, playlistsLoaded]);

  useEffect(() => {
    if (!currentUser || !playlistsLoaded || isSyncingRef.current) return;
    const id = setTimeout(() => saveGigPlaylists(currentUser.uid, gigPlaylists).catch(console.error), 2000);
    return () => clearTimeout(id);
  }, [gigPlaylists, currentUser, playlistsLoaded]);

  useEffect(() => {
    if (!currentUser) return;
    const gigsQuery = query(collection(db, 'liveGigs'), where('artistId', '==', currentUser.uid));
    const unsub = onSnapshot(gigsQuery, { includeMetadataChanges: false }, snap => {
      const gigsData = snap.docs.map(d => {
        const data = d.data();
        if (data.location && data.location.latitude != null)
          data.location = { lat: data.location.latitude, lng: data.location.longitude };
        return { id: d.id, ...data };
      });
      gigsData.sort((a, b) => {
        const dateA = a.gigDate || a.date || '';
        const dateB = b.gigDate || b.date || '';
        if (dateA !== dateB) return dateB.localeCompare(dateA);
        return (b.gigTime || b.time || '').localeCompare(a.gigTime || a.time || '');
      });
      setGigs(gigsData);
    }, err => console.error('Firebase listener error:', err));
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const profileRef = doc(db, 'artistProfiles', currentUser.uid);
    const unsub = onSnapshot(profileRef, snap => {
      if (snap.exists()) setGigwaveFollowerCount(snap.data().gigwaveFollowerCount || 0);
    });
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unsub = listenHireRequestsForArtist(currentUser.uid, setHireRequests);
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    const unsub = listenBookingsForArtist(currentUser.uid, setArtistBookings);
    return () => unsub();
  }, [currentUser]);

  // ─── Load analytics when tab is opened ───────────────────
  useEffect(() => {
    if (tab !== 'analytics' || !currentUser) return;
    if (analytics) return; // already loaded
    setLoadingAnalytics(true);
    getGigAnalytics(currentUser.uid)
      .then(data => setAnalytics(data))
      .catch(console.error)
      .finally(() => setLoadingAnalytics(false));
  }, [tab, currentUser]);

  // ─── Load earnings when tab is opened ────────────────────
  useEffect(() => {
    if (tab !== 'earnings' || !currentUser) return;
    if (earnings) return; // already loaded
    setLoadingEarnings(true);
    getEarningsData(currentUser.uid)
      .then(data => setEarnings(data))
      .catch(console.error)
      .finally(() => setLoadingEarnings(false));
  }, [tab, currentUser]);

  // ─── Load blocked dates when availability tab opened ─────
  useEffect(() => {
    if (tab !== 'avail_cal' || !currentUser) return;
    if (availLoaded) return;
    getBlockedDates(currentUser.uid)
      .then(dates => { setBlockedDates(dates); setAvailLoaded(true); })
      .catch(console.error);
  }, [tab, currentUser]);

  // ─── iTunes search ────────────────────────────────────────
  const searchItunesAPI = async () => {
    if (!itunesSearch.trim()) { alert('Please enter a song or artist name!'); return; }
    try {
      setSearchingItunes(true);
      const response = await fetch(
        `https://itunes.apple.com/search?term=${encodeURIComponent(itunesSearch)}&entity=song&limit=20`
      );
      if (!response.ok) throw new Error('iTunes search failed');
      const data = await response.json();
      if (data.resultCount === 0) { alert('No songs found. Try a different search!'); setItunesResults([]); return; }
      setItunesResults(data.results.map((track, i) => ({
        id: Date.now() + i,
        title: track.trackName, artist: track.artistName, album: track.collectionName,
        duration: track.trackTimeMillis
          ? `${Math.floor(track.trackTimeMillis / 60000)}:${String(Math.floor((track.trackTimeMillis % 60000) / 1000)).padStart(2, '0')}`
          : '',
        artwork: track.artworkUrl100, previewUrl: track.previewUrl, itunesId: track.trackId
      })));
    } catch (err) { alert('Error searching iTunes: ' + err.message); }
    finally { setSearchingItunes(false); }
  };

  const addItunesSongToMaster = (song) => {
    if (masterSongs.find(s => s.title === song.title && s.artist === song.artist)) {
      alert('⚠️ Already in your master playlist!'); return;
    }
    setAddSongModal({ mode: 'itunes', song });
  };

  const addToMaster = () => setAddSongModal({ mode: 'manual' });

  const handleAddSongConfirm = (newSong) => {
    setMasterSongs(prev => [newSong, ...prev]);
    if (addSongModal?.mode === 'itunes')
      setItunesResults(prev => prev.filter(s => s.id !== addSongModal.song.id));
    setAddSongModal(null);
  };

  const removeFromMaster = (id) => {
    if (window.confirm('Remove this song?')) setMasterSongs(prev => prev.filter(s => s.id !== id));
  };

  const createGigPlaylist = () => {
    setEditingPlaylist({ id: Date.now(), name: 'New Playlist', description: '', songs: [], createdAt: new Date() });
    setShowPlaylistModal(true);
  };
  const saveGigPlaylist = () => {
    if (!editingPlaylist.name.trim()) { alert('Please enter a playlist name!'); return; }
    const existing = gigPlaylists.find(p => p.id === editingPlaylist.id);
    if (existing) setGigPlaylists(prev => prev.map(p => p.id === editingPlaylist.id ? editingPlaylist : p));
    else setGigPlaylists(prev => [...prev, editingPlaylist]);
    setShowPlaylistModal(false); setEditingPlaylist(null);
  };
  const deleteGigPlaylist = (id) => {
    if (window.confirm('Delete this playlist?')) setGigPlaylists(prev => prev.filter(p => p.id !== id));
  };

  const createGig = () => {
    setEditingGig({ id: Date.now(), venueName: '', address: '', location: null, date: '', time: '', playlistId: null, queueSize: 20, status: 'upcoming' });
    setShowGigModal(true);
  };
  const captureGPSLocation = async () => {
    setCapturingGPS(true);
    try {
      const location = await getUserLocation();
      setEditingGig(prev => ({ ...prev, location, address: `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` }));
      alert('✅ GPS location captured!');
    } catch (err) { alert('❌ ' + err.message); }
    finally { setCapturingGPS(false); }
  };

  const saveGig = async () => {
    if (savingGig) return;
    if (!editingGig.venueName || !editingGig.date || !editingGig.time) { alert('Please fill in venue name, date, and time!'); return; }
    const queueSize = editingGig.queueSize || 20;
    if (queueSize < 20 || queueSize > 50) { alert('⚠️ Queue size must be between 20 and 50!'); return; }
    setSavingGig(true);
    try {
      let location = editingGig.location;
      if (!location) {
        if (window.confirm('No venue location. Use your current location?')) location = await getUserLocation();
        else { alert('Please set a venue location!'); return; }
      }
      const gigDateTime = new Date(`${editingGig.date}T${editingGig.time}`);
      const hasConflict = gigs.some(g => {
        if (g.id === editingGig.id) return false;
        const status = calculateGigStatus(g);
        if (status === 'ended' || status === 'cancelled') return false;
        const existingDT = new Date(`${g.gigDate || g.date}T${g.gigTime || g.time}`);
        return Math.abs(gigDateTime - existingDT) < 6 * 60 * 60 * 1000;
      });
      if (hasConflict) { alert('⚠️ You already have a gig within 6 hours of this time!'); return; }
      let queuedSongs = [];
      if (editingGig.playlistId) {
        const playlist = gigPlaylists.find(p => p.id === editingGig.playlistId);
        queuedSongs = playlist?.songs?.length > 0 ? playlist.songs.map(id => masterSongs.find(s => s.id === id)).filter(Boolean) : masterSongs;
      } else { queuedSongs = masterSongs; }
      const gigData = {
        artistId: currentUser.uid, artistName: currentUser.displayName || 'Artist', artistProfile,
        venueName: editingGig.venueName, venueAddress: editingGig.address || '', location,
        status: 'upcoming', gigDate: editingGig.date, gigTime: editingGig.time,
        playlistId: editingGig.playlistId || null, queueSize: editingGig.queueSize || 20,
        queuedSongs: queuedSongs.slice(0, editingGig.queueSize || 20),
        masterPlaylist: masterSongs, notes: editingGig.notes || ''
      };
      const existingGig = gigs.find(g => g.id === editingGig.id);
      const isEdit = existingGig && typeof editingGig.id === 'string' && editingGig.id.length > 10;
      if (isEdit)
        await updateDoc(doc(db, 'liveGigs', editingGig.id), gigData);
      else {
        await createLiveGig(gigData, currentUser.uid);
        notifyFollowers(currentUser.uid, {
          type: 'new_gig',
          artistName: gigData.artistName,
          venueName: gigData.venueName,
          gigDate: gigData.gigDate,
          gigTime: gigData.gigTime,
          message: `🎸 ${gigData.artistName} has a new gig at ${gigData.venueName} on ${new Date(gigData.gigDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} at ${gigData.gigTime}`,
        }).catch(console.warn);
      }
      setShowGigModal(false); setEditingGig(null);
      // Reset analytics cache so next visit reloads
      setAnalytics(null);
      alert('✅ Gig saved!');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSavingGig(false); }
  };

  const deleteGig = async (gigId) => {
    if (window.confirm('Delete this gig permanently?')) {
      try {
        await deleteDoc(doc(db, 'liveGigs', String(gigId)));
        if (editingGig?.id === gigId) { setEditingGig(null); setShowGigModal(false); }
        setAnalytics(null);
        alert('✅ Gig deleted!');
      } catch (err) { alert('❌ ' + err.message); }
    }
  };

  const handleGoLive = async (gig) => {
    try {
      const existingLiveGig = await getActiveLiveGigForArtist(currentUser.uid);
      if (existingLiveGig && existingLiveGig.id !== gig.id) {
        if (!window.confirm(`⚠️ Active gig at "${existingLiveGig.venueName}". End it and start this one?`)) return;
        await firebaseEndLiveGig(existingLiveGig.id);
      }
    } catch (err) { console.warn(err); }
    let location = { ...gig }.location;
    if (location?.latitude != null) location = { lat: location.latitude, lng: location.longitude };
    if (!location) {
      if (window.confirm('No venue location. Use current location?')) location = await getUserLocation();
      else { alert('Please set a venue location first!'); return; }
    }
    const maxQueueSize = gig.queueSize || 20;
    let queuedSongs = [];
    if (gig.playlistId) {
      const playlist = gigPlaylists.find(p => p.id === gig.playlistId);
      if (playlist?.songs?.length > 0) queuedSongs = playlist.songs.map(id => masterSongs.find(s => s.id === id)).filter(Boolean);
    }
    if (queuedSongs.length === 0) queuedSongs = [...masterSongs];
    if (queuedSongs.length < maxQueueSize) {
      const existing = queuedSongs.map(s => s.id);
      queuedSongs = [...queuedSongs, ...masterSongs.filter(s => !existing.includes(s.id)).slice(0, maxQueueSize - queuedSongs.length)];
    }
    notifyFollowers(currentUser.uid, {
      type: 'artist_live',
      artistName: artistProfile?.artistName || currentUser.displayName || 'Your artist',
      venueName: gig.venueName,
      gigId: gig.id,
      message: `🔴 ${artistProfile?.artistName || currentUser.displayName || 'Your artist'} is LIVE now at ${gig.venueName}! Join them on GigWave.`,
    }).catch(console.warn);
    onGoLive({ ...gig, location, queuedSongs: queuedSongs.slice(0, maxQueueSize), masterPlaylist: masterSongs });
  };

  const handleToggleProfile = async () => {
    const isCurrentlyDisabled = artistProfile?.profileDisabled === true;
    if (!window.confirm(
      isCurrentlyDisabled
        ? '✅ Re-enable your public profile? It will be visible at gigwave.in/artist/...'
        : '⚠️ Disable your public profile? It will be hidden from venues and the public URL will show "Artist not found".'
    )) return;
    setTogglingProfile(true);
    try {
      await setProfileDisabled(currentUser.uid, !isCurrentlyDisabled);
      setArtistProfile(prev => prev ? { ...prev, profileDisabled: !isCurrentlyDisabled } : prev);
      alert(isCurrentlyDisabled ? '✅ Profile is now public!' : '🔒 Profile hidden. Your public page now shows "Artist not found".');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setTogglingProfile(false); }
  };

  const handleEditProfile = () => {
    if (artistProfile) setProfileData({
      ...artistProfile,
      priceMin: artistProfile.priceMin || '',
      priceMax: artistProfile.priceMax || '',
      availabilityNote: artistProfile.availabilityNote || '',
      isAvailable: artistProfile.isAvailable !== false,
      socialMedia: artistProfile.socialMedia || { instagram: '', facebook: '', youtube: '', spotify: '', twitter: '', tiktok: '', soundcloud: '' }
    });
    setShowProfileSetup(true);
  };
  const handleSaveProfile = async () => {
    try {
      await saveArtistProfile(currentUser.uid, profileData);
      await publishArtistProfile(currentUser.uid, profileData);
      setArtistProfile(profileData);
      setShowProfileSetup(false);
      alert('✅ Profile saved!');
    } catch (err) { alert('Error: ' + err.message); }
  };

  const checkFollowerReminder = (profile) => {
    const snoozedUntil = localStorage.getItem(`GigWave_followerSnooze_${currentUser?.uid}`);
    if (snoozedUntil && Date.now() < parseInt(snoozedUntil)) return;
    const lastUpdated = profile?.followerCountsUpdatedAt;
    if (!lastUpdated) {
      setTimeout(() => { setFollowerModalMode('reminder'); setShowFollowerModal(true); }, 2000);
      return;
    }
    const lastMs = lastUpdated?.seconds ? lastUpdated.seconds * 1000 : new Date(lastUpdated).getTime();
    if (Date.now() - lastMs > 30 * 24 * 60 * 60 * 1000) {
      setTimeout(() => { setFollowerModalMode('reminder'); setShowFollowerModal(true); }, 2000);
    }
  };

  const handleSnoozeFollower = () => {
    localStorage.setItem(`GigWave_followerSnooze_${currentUser?.uid}`, (Date.now() + 7 * 24 * 60 * 60 * 1000).toString());
    setShowFollowerModal(false);
  };

  const handleSaveFollowerCounts = async () => {
    setSavingFollowers(true);
    try {
      await saveFollowerCounts(currentUser.uid, followerCounts);
      const updatedProfile = { ...(artistProfile || {}), followerCounts, followerCountsUpdatedAt: { seconds: Date.now() / 1000 } };
      await publishArtistProfile(currentUser.uid, updatedProfile);
      setArtistProfile(prev => prev ? { ...prev, followerCounts, followerCountsUpdatedAt: { seconds: Date.now() / 1000 } } : prev);
      localStorage.removeItem(`GigWave_followerSnooze_${currentUser?.uid}`);
      setShowFollowerModal(false);
      alert('✅ Follower counts saved!');
    } catch (err) { alert('Error: ' + err.message); }
    finally { setSavingFollowers(false); }
  };

  const handleArtistRespond = async (requestId, action) => {
    try {
      const budget = action === 'counter' ? Number(counterForm.budget) : null;
      if (action === 'counter' && !budget) { alert('Enter a budget amount!'); return; }
      await artistRespondToRequest(requestId, action, budget, counterForm.message);
      setRespondingTo(null);
      setCounterForm({ budget: '', message: '' });
      alert(action === 'accept' ? '✅ Accepted! Booking confirmed!' : action === 'counter' ? '✅ Counter sent!' : '✅ Declined.');
    } catch (err) { alert('Error: ' + err.message); }
  };

  // ============================================
  // RENDER
  // ============================================
  const statusColors = { upcoming: 'badge-upcoming', checkVenue: 'badge-upcoming', live: 'badge-live', ended: 'badge-ended', cancelled: 'badge-ended' };
  const statusLabel = { upcoming: '🔵 Upcoming', checkVenue: '🟠 Check With Venue', live: '🟢 Live', ended: '⚫ Ended', cancelled: '🔴 Cancelled' };

  const sortedMaster = masterSongs.slice().sort((a, b) => {
    if (masterSortBy === 'song') return a.title.localeCompare(b.title);
    if (masterSortBy === 'artist') return a.artist.localeCompare(b.artist);
    return 0;
  });

  const trackedCount = sortedMaster.filter(s => findBankEntry(s, songBank)?.youtubeId).length;
  const pendingHireCount = hireRequests.filter(r => r.status === 'pending').length;
  const endedGigsCount = gigs.filter(g => calculateGigStatus(g) === 'ended').length;

  const hireStatusColor = { pending: '#3b82f6', countered: '#f59e0b', accepted: '#22c55e', declined: '#ef4444' };
  const hireStatusLabel = { pending: '⏳ Awaiting Your Response', countered: '🔄 Venue Reviewing Counter', accepted: '✅ Accepted', declined: '❌ Declined' };

  return (
    <div data-testid="artist-dashboard" className="artist-dashboard rock-background min-h-screen p-4 pb-24 md:pb-4">

      {showGigModal && (
        <GigModal show={showGigModal} onClose={() => setShowGigModal(false)}
          editingGig={editingGig} setEditingGig={setEditingGig} gigPlaylists={gigPlaylists}
          gigs={gigs} saveGig={saveGig} deleteGig={deleteGig} savingGig={savingGig}
          captureGPSLocation={captureGPSLocation} capturingGPS={capturingGPS} />
      )}

      {addSongModal && (
        <AddSongModal mode={addSongModal.mode} song={addSongModal.song || null}
          onConfirm={handleAddSongConfirm} onClose={() => setAddSongModal(null)} />
      )}

      <div className="max-w-6xl mx-auto pt-6">

        {artistProfile?.followerCountsBlocked && (
          <div className="mb-6 p-4 rounded-xl flex flex-col md:flex-row md:items-center gap-3"
            style={{ background: 'rgba(239,68,68,0.12)', border: '2px solid rgba(239,68,68,0.5)' }}>
            <div className="flex-1">
              <p className="text-red-400 font-bold text-lg">⚠️ Your follower counts have been hidden</p>
              <p className="text-red-300 text-sm mt-1">An admin has hidden your follower counts. To reinstate, email us with screenshots.</p>
            </div>
            <a href="mailto:gig.wave.2005@gmail.com?subject=Follower Count Verification - GigWave"
              className="btn btn-fire whitespace-nowrap text-sm flex-shrink-0">📧 Contact Support</a>
          </div>
        )}

        {/* Header */}
        <div className="artist-header-card gig-card mb-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="artist-dashboard-title mb-3">🎸 ARTIST DASHBOARD</h1>
              <p data-testid="artist-name" className="artist-welcome">Welcome, {currentUser?.displayName || currentUser?.email}</p>
              {gigwaveFollowerCount > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-sm font-bold px-3 py-1 rounded-full"
                    style={{ background: 'rgba(168,85,247,0.15)', color: '#c084fc', border: '1px solid rgba(168,85,247,0.4)' }}>
                    👥 {gigwaveFollowerCount.toLocaleString()} GigWave {gigwaveFollowerCount === 1 ? 'follower' : 'followers'}
                  </span>
                </div>
              )}
              {/* Public profile link */}
              {artistProfile?.artistName && (() => {
                const slug = (artistProfile.artistName || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 60);
                const url = `https://gigwave.in/artist/${slug}`;
                return (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="text-xs text-gray-500">🔗 Your public page:</span>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="text-xs font-bold hover:underline"
                      style={{ color: '#00d4ff' }}>{url}</a>
                    <button
                      onClick={() => navigator.clipboard.writeText(url).then(() => alert('✅ Link copied!')).catch(() => prompt('Copy link:', url))}
                      className="text-xs px-2 py-0.5 rounded font-bold"
                      style={{ background: 'rgba(0,212,255,0.12)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.3)' }}>
                      📋 Copy
                    </button>
                  </div>
                );
              })()}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={handleEditProfile} className="btn btn-electric w-full sm:w-auto">
                <Edit2 size={20} /><span>Edit Profile</span>
              </button>
              <button
                onClick={handleToggleProfile}
                disabled={togglingProfile}
                className="btn w-full sm:w-auto text-sm"
                style={artistProfile?.profileDisabled
                  ? { background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)', boxShadow: '0 0 10px rgba(34,197,94,0.2)' }
                  : { background: '#dc2626', color: '#ffffff', border: '1px solid #ef4444' }
                }>
                {togglingProfile ? '⏳' : artistProfile?.profileDisabled ? '👁️ Enable Profile' : '🔒 Hide Profile'}
              </button>
              <button onClick={() => onModeChange('discover')} className="btn btn-ghost w-full sm:w-auto">← Back</button>
              {currentUser?.email === 'gig.wave.2005@gmail.com' && (
                <button onClick={() => onModeChange('admin')}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-bold transition-all w-full sm:w-auto">
                  🔐 Admin Dashboard
                </button>
              )}
              <button onClick={async () => { await signOutUser(); onModeChange('discover'); }} className="btn btn-fire w-full sm:w-auto">
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Follower counts strip */}
        {artistProfile && !artistProfile.followerCountsBlocked && (
          <div className="gig-card mb-8 px-6 py-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-white font-bold text-sm">📊 My Follower Counts</p>
                  {artistProfile.followerCountsUpdatedAt && (
                    <span className="text-xs text-gray-500">
                      · Updated {(() => {
                        const t = artistProfile.followerCountsUpdatedAt?.seconds
                          ? new Date(artistProfile.followerCountsUpdatedAt.seconds * 1000)
                          : new Date(artistProfile.followerCountsUpdatedAt);
                        return t.toLocaleDateString();
                      })()}
                    </span>
                  )}
                </div>
                {artistProfile.followerCounts && Object.values(artistProfile.followerCounts).some(v => v) ? (
                  <div className="flex flex-wrap gap-3">
                    {[
                      { key: 'instagram', icon: '📸', color: '#E1306C', label: 'Instagram' },
                      { key: 'youtube', icon: '▶️', color: '#FF0000', label: 'YouTube' },
                      { key: 'spotify', icon: '🎵', color: '#1DB954', label: 'Spotify' },
                      { key: 'facebook', icon: '👍', color: '#1877F2', label: 'Facebook' },
                      { key: 'tiktok', icon: '🎵', color: '#ee1d52', label: 'TikTok' },
                      { key: 'twitter', icon: '🐦', color: '#1DA1F2', label: 'Twitter' },
                      { key: 'soundcloud', icon: '☁️', color: '#FF5500', label: 'SoundCloud' },
                    ].map(({ key, icon, color, label }) => {
                      const val = artistProfile.followerCounts[key];
                      if (!val) return null;
                      return (
                        <div key={key} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
                          style={{ background: `${color}15`, border: `1px solid ${color}40` }}>
                          <span className="text-sm">{icon}</span>
                          <span className="text-white font-bold text-sm">{Number(val).toLocaleString()}</span>
                          <span className="text-xs" style={{ color }}>{label}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No follower counts added yet.</p>
                )}
              </div>
              <button onClick={() => { setFollowerModalMode('edit'); setShowFollowerModal(true); }}
                className="btn btn-ghost text-sm whitespace-nowrap flex-shrink-0">✏️ Update Counts</button>
            </div>
          </div>
        )}

        {/* Nav Tabs */}
        <div className="artist-nav-tabs">
          {[
            { key: 'master', icon: <Music className="inline mr-2" size={24} />, label: 'Master Playlist' },
            { key: 'playlists', icon: <Music className="inline mr-2" size={24} />, label: 'Gig Playlists' },
            { key: 'gigs', icon: <Calendar className="inline mr-2" size={24} />, label: 'My Gigs' },
            { key: 'analytics', icon: <span className="inline mr-2">📊</span>, label: `Analytics${endedGigsCount > 0 ? ` (${endedGigsCount})` : ''}` },
            { key: 'hire', icon: <span className="inline mr-2">🏢</span>, label: `Hire Requests${pendingHireCount > 0 ? ` (${pendingHireCount})` : ''}` },
            { key: 'earnings', icon: <span className="inline mr-2">💰</span>, label: 'Earnings' },
            { key: 'avail_cal', icon: <span className="inline mr-2">🗓️</span>, label: 'Availability' },
          ].map(({ key, icon, label }) => (
            <button key={key} onClick={() => setTab(key)} className={`artist-nav-tab ${tab === key ? 'active' : ''}`}>
              {icon}<span>{label}</span>
            </button>
          ))}
        </div>

        {/* ══ MASTER PLAYLIST TAB ══ */}
        {tab === 'master' && (
          <div className="space-y-6">
            <div className="artist-search-section">
              <h3 className="artist-section-header">
                <span className="emoji-icon">🎵</span><span>SEARCH ITUNES MUSIC</span>
              </h3>
              <div className="flex flex-col md:flex-row gap-3 mb-4">
                <input type="text" value={itunesSearch} onChange={e => setItunesSearch(e.target.value)}
                  onKeyPress={e => e.key === 'Enter' && searchItunesAPI()}
                  placeholder="Search for songs, artists, albums..."
                  data-testid="song-search"
                  className="artist-search-input flex-1" disabled={searchingItunes} />
                <button onClick={searchItunesAPI} disabled={searchingItunes} className="btn btn-neon">
                  <span>{searchingItunes ? '⏳' : '🔍'}</span><span>Search</span>
                </button>
              </div>
              {itunesResults.length > 0 && (
                <div data-testid="search-results" className="space-y-2 max-h-96 overflow-y-auto">
                  <p className="text-white font-bold text-lg mb-3">🎼 Found <span className="text-neon">{itunesResults.length}</span> songs</p>
                  {itunesResults.map(song => (
                    <div key={song.id} className="artist-itunes-result flex flex-col md:flex-row md:items-center gap-4">
                      {song.artwork && <img src={song.artwork} alt={song.title} className="w-16 h-16 rounded object-cover border-2 border-electric" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-bold text-lg truncate">{song.title}</div>
                        <div className="text-electric text-sm font-semibold">{song.artist}</div>
                        {song.album && <div className="text-gray-light text-xs truncate">{song.album}</div>}
                        {song.duration && <div className="text-neon text-xs font-semibold">⏱️ {song.duration}</div>}
                        {findBankEntry(song, songBank)?.youtubeId && (
                          <span className="inline-block text-[10px] px-1.5 py-0.5 rounded mt-1"
                            style={{ background: 'rgba(124,58,237,0.25)', color: '#c4b5fd' }}>🎵 Track in bank</span>
                        )}
                      </div>
                      <button onClick={() => addItunesSongToMaster(song)} className="btn btn-neon text-sm whitespace-nowrap">
                        <Plus size={18} /><span>Add to Master</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {searchingItunes && <div className="text-center py-8"><div className="text-electric text-lg loading-pulse font-bold">🔍 Searching iTunes...</div></div>}
            </div>

            <div className="artist-playlist-section">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
                <div>
                  <h2 className="artist-section-header mb-0"><span className="emoji-icon">🎵</span><span>MASTER PLAYLIST</span></h2>
                  {masterSongs.length > 0 && (
                    <p className="text-xs mt-1" style={{ color: trackedCount > 0 ? '#c4b5fd' : '#6b7280' }}>
                      🎵 {trackedCount} of {masterSongs.length} songs have backing tracks
                    </p>
                  )}
                </div>
                <div className="flex gap-3">
                  <select value={masterSortBy} onChange={e => setMasterSortBy(e.target.value)} className="artist-dropdown">
                    <option value="default">Default Order</option>
                    <option value="song">Sort by Song (A-Z)</option>
                    <option value="artist">Sort by Artist (A-Z)</option>
                  </select>
                  <button onClick={addToMaster} className="btn btn-electric"><Plus size={20} /><span>Add Song</span></button>
                </div>
              </div>
              {masterSongs.length === 0 ? (
                <div className="artist-empty-state"><p className="artist-empty-text">No songs yet. Click "Add Song" to start! 🎸</p></div>
              ) : (
                <div className="space-y-3">
                  {sortedMaster.map(song => {
                    const bankEntry = findBankEntry(song, songBank);
                    return (
                      <div key={song.id} className="artist-song-card">
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="artist-song-title truncate">{song.title}</div>
                            <div className="artist-song-artist truncate">{song.artist}</div>
                            <BackingTrackEditor song={song} bankEntry={bankEntry} onSaved={() => setBankRefresh(n => n + 1)} />
                          </div>
                          <button onClick={() => removeFromMaster(song.id)}
                            className="text-red-400 hover:text-red-300 p-2 touch-target ml-3 flex-shrink-0">
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ GIG PLAYLISTS TAB ══ */}
        {tab === 'playlists' && (
          <div className="space-y-6">
            <div className="artist-section-card">
              <h2 className="artist-section-header"><span className="emoji-icon">🎸</span><span>GIG PLAYLISTS</span></h2>
              <button onClick={createGigPlaylist} className="btn btn-electric mb-6"><Plus size={20} /><span>Create Playlist</span></button>
              {gigPlaylists.length === 0 ? (
                <div className="artist-empty-state"><p className="artist-empty-text">No playlists yet. Create custom setlists! 🎵</p></div>
              ) : (
                <div className="space-y-4">
                  {gigPlaylists.map(playlist => (
                    <div key={playlist.id} className="artist-song-card p-6">
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-white mb-2">{playlist.name}</h3>
                          {playlist.description && <p className="text-gray-light text-sm mb-2">{playlist.description}</p>}
                          <p className="text-electric font-bold text-lg">🎵 {playlist.songs.length} songs</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => { setEditingPlaylist(playlist); setShowPlaylistModal(true); }} className="btn btn-electric text-sm"><Edit2 size={16} /><span>Edit</span></button>
                          <button onClick={() => deleteGigPlaylist(playlist.id)} className="btn btn-fire text-sm"><Trash2 size={16} /></button>
                        </div>
                      </div>
                      {playlist.songs.length > 0 && (
                        <div className="space-y-1 mt-4 pt-4 border-t border-white/20">
                          <p className="text-white font-bold mb-2">Songs:</p>
                          {playlist.songs.slice(0, 5).map((songId, index) => {
                            const song = masterSongs.find(s => s.id === songId);
                            return song ? (
                              <div key={songId} className="text-gray-light text-sm flex items-center gap-2">
                                <span className="text-electric font-bold">{index + 1}.</span>
                                <span className="text-white font-semibold">{song.title}</span>
                                <span className="text-gray">-</span>
                                <span className="text-electric">{song.artist}</span>
                              </div>
                            ) : null;
                          })}
                          {playlist.songs.length > 5 && <div className="text-neon text-sm font-bold mt-2">+{playlist.songs.length - 5} more...</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ MY GIGS TAB ══ */}
        {tab === 'gigs' && (
          <div className="space-y-6">
            <div className="artist-section-card">
              <h2 className="artist-section-header"><span className="emoji-icon">⚡</span><span>MY GIGS</span></h2>
              <button onClick={createGig} className="btn btn-electric mb-6"><Plus size={20} /><span>Create Gig</span></button>
              {gigs.length === 0 ? (
                <div className="artist-empty-state"><p className="artist-empty-text">No gigs yet. Click "Create Gig"! ⚡</p></div>
              ) : (
                <div className="space-y-4">
                  {gigs.map(gig => {
                    const playlist = gigPlaylists.find(p => p.id === gig.playlistId);
                    const currentStatus = calculateGigStatus(gig);
                    const isEnded = currentStatus === 'ended' || currentStatus === 'cancelled';
                    return (
                      <div key={gig.id} className="artist-song-card p-6">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                              <h3 className="text-2xl font-bold text-white">{gig.venueName}</h3>
                              <span className={`status-badge ${statusColors[currentStatus] || 'badge-upcoming'}`}>
                                {statusLabel[currentStatus] || '🔵 Upcoming'}
                              </span>
                            </div>
                            <div className="space-y-1 text-sm">
                              <p className="text-electric font-bold text-lg">📅 {gig.gigDate || gig.date} at {gig.gigTime || gig.time}</p>
                              {(gig.venueAddress || gig.address) && <p className="text-gray-light">📍 {gig.venueAddress || gig.address}</p>}
                              {gig.location?.lat != null && <p className="text-neon text-xs font-semibold">✅ {gig.location.lat.toFixed(4)}°, {gig.location.lng.toFixed(4)}°</p>}
                              {playlist && <p className="text-neon font-bold">🎵 {playlist.name} ({playlist.songs.length} songs)</p>}
                              {gig.interestedCount > 0 && <p className="text-neon font-bold">👥 {gig.interestedCount} interested</p>}
                              {isEnded && <p className="text-red-400 font-bold mt-2">⚠️ This gig has {currentStatus === 'cancelled' ? 'been cancelled' : 'ended'}</p>}
                            </div>
                          </div>
                          {gig.artistProfile?.socialMedia && (
                            <div className="mt-4 pt-4 border-t border-white/20">
                              <SocialFollowButtons socialMedia={gig.artistProfile.socialMedia} artistName={gig.artistName} />
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2">
                            {!isEnded && (
                              <>
                                <button onClick={() => { setEditingGig(gig); setShowGigModal(true); }} className="btn btn-electric text-sm"><Edit2 size={16} /><span>Edit</span></button>
                                <button onClick={() => handleGoLive(gig)} className="btn btn-fire text-sm">🔴 Go Live</button>
                              </>
                            )}
                            <button onClick={() => deleteGig(gig.id)} className="btn btn-ghost text-sm"><Trash2 size={16} /><span>Delete</span></button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ ANALYTICS TAB ══ */}
        {tab === 'analytics' && (
          <div className="space-y-6">
            <div className="artist-section-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="artist-section-header mb-0"><span className="emoji-icon">📊</span><span>POST-GIG ANALYTICS</span></h2>
                <button
                  onClick={() => { setAnalytics(null); setLoadingAnalytics(true); getGigAnalytics(currentUser.uid).then(setAnalytics).finally(() => setLoadingAnalytics(false)); }}
                  className="btn btn-ghost text-sm">🔄 Refresh</button>
              </div>

              {loadingAnalytics && (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-electric mx-auto mb-4"></div>
                  <p className="text-electric font-bold">Loading your analytics...</p>
                </div>
              )}

              {!loadingAnalytics && analytics?.gigStats?.length === 0 && (
                <div className="artist-empty-state">
                  <p className="text-4xl mb-3">📊</p>
                  <p className="artist-empty-text">No ended gigs yet. Analytics will appear here after your first gig ends!</p>
                </div>
              )}

              {!loadingAnalytics && analytics?.summary && analytics.gigStats.length > 0 && (
                <>
                  {/* Overall summary */}
                  <div className="mb-8">
                    <p className="text-electric text-xs font-bold uppercase tracking-wider mb-4">Overall Summary</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <StatCard emoji="🎸" label="Gigs Performed" value={analytics.summary.totalGigsPerformed} color="#00d4ff" />
                      <StatCard emoji="👥" label="Total Audience" value={analytics.summary.totalAudience.toLocaleString()} color="#a855f7" />
                      <StatCard emoji="⚡" label="Total Votes" value={analytics.summary.totalVotes.toLocaleString()} color="#f59e0b" />
                      <StatCard emoji="🎵" label="Song Requests" value={analytics.summary.totalRequests.toLocaleString()} color="#22c55e" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <StatCard emoji="⭐" label="Avg Rating"
                        value={analytics.summary.avgRating ? `${analytics.summary.avgRating}/5` : 'N/A'}
                        color="#f59e0b" />
                      <StatCard emoji="💰" label="Total Tips"
                        value={analytics.summary.totalTips > 0 ? `₹${analytics.summary.totalTips.toLocaleString()}` : '₹0'}
                        color="#22c55e" />
                      {analytics.summary.bestGig && (
                        <StatCard emoji="🏆" label="Best Gig (audience)"
                          value={analytics.summary.bestGig.audienceCount}
                          sub={analytics.summary.bestGig.venueName}
                          color="#f59e0b" />
                      )}
                    </div>
                  </div>

                  {/* Per-gig breakdown */}
                  <p className="text-electric text-xs font-bold uppercase tracking-wider mb-4">Per-Gig Breakdown</p>
                  <div className="space-y-4">
                    {analytics.gigStats.map(gig => (
                      <div key={gig.id} className="rounded-xl overflow-hidden"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>

                        {/* Gig header */}
                        <button
                          onClick={() => setExpandedGigId(expandedGigId === gig.id ? null : gig.id)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition">
                          <div>
                            <p className="text-white font-bold text-lg">{gig.venueName}</p>
                            <p className="text-gray-400 text-sm">📅 {gig.gigDate} {gig.gigTime && `at ${gig.gigTime}`}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            {/* Quick stats pills */}
                            <div className="hidden md:flex gap-2">
                              <span className="text-xs px-2 py-1 rounded-full font-bold"
                                style={{ background: 'rgba(168,85,247,0.15)', color: '#c084fc' }}>
                                👥 {gig.audienceCount}
                              </span>
                              <span className="text-xs px-2 py-1 rounded-full font-bold"
                                style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>
                                ⚡ {gig.totalVotes}
                              </span>
                              {gig.avgRating && (
                                <span className="text-xs px-2 py-1 rounded-full font-bold"
                                  style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24' }}>
                                  ⭐ {gig.avgRating}
                                </span>
                              )}
                              {gig.totalTips > 0 && (
                                <span className="text-xs px-2 py-1 rounded-full font-bold"
                                  style={{ background: 'rgba(34,197,94,0.15)', color: '#4ade80' }}>
                                  💰 ₹{gig.totalTips.toLocaleString()}
                                </span>
                              )}
                            </div>
                            <span className="text-gray-400 text-lg">{expandedGigId === gig.id ? '▲' : '▼'}</span>
                          </div>
                        </button>

                        {/* Expanded detail */}
                        {expandedGigId === gig.id && (
                          <div className="px-5 pb-5 border-t border-white/10">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 mb-5">
                              <StatCard emoji="👥" label="Audience" value={gig.audienceCount} color="#a855f7" />
                              <StatCard emoji="⚡" label="Votes" value={gig.totalVotes} color="#f59e0b" />
                              <StatCard emoji="🎵" label="Requests" value={gig.totalRequests} color="#00d4ff" />
                              <StatCard emoji="⭐" label="Avg Rating"
                                value={gig.avgRating ? `${gig.avgRating}/5` : 'N/A'}
                                sub={gig.ratingCount > 0 ? `${gig.ratingCount} ratings` : ''}
                                color="#f59e0b" />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Most voted song */}
                              {gig.mostVotedSong ? (
                                <div className="p-4 rounded-xl"
                                  style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}>
                                  <p className="text-yellow-500 text-xs font-bold uppercase tracking-wider mb-2">🏆 Most Voted Song</p>
                                  <p className="text-white font-bold">{gig.mostVotedSong.title}</p>
                                  <p className="text-gray-400 text-sm">{gig.mostVotedSong.artist}</p>
                                  <p className="text-yellow-400 font-bold text-sm mt-1">⚡ {gig.mostVotedSong.votes} votes</p>
                                </div>
                              ) : (
                                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                  <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">🏆 Most Voted Song</p>
                                  <p className="text-gray-500 text-sm">No votes recorded</p>
                                </div>
                              )}

                              {/* Most requested song */}
                              {gig.mostRequested ? (
                                <div className="p-4 rounded-xl"
                                  style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
                                  <p className="text-electric text-xs font-bold uppercase tracking-wider mb-2">🎵 Most Requested Song</p>
                                  <p className="text-white font-bold">{gig.mostRequested.title}</p>
                                  <p className="text-gray-400 text-sm">{gig.mostRequested.artist}</p>
                                  <p className="text-electric font-bold text-sm mt-1">🎵 Requested {gig.mostRequested.count}x</p>
                                </div>
                              ) : (
                                <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                                  <p className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-2">🎵 Most Requested Song</p>
                                  <p className="text-gray-500 text-sm">No requests recorded</p>
                                </div>
                              )}
                            </div>

                            {/* Tips */}
                            {gig.totalTips > 0 && (
                              <div className="mt-4 p-4 rounded-xl"
                                style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}>
                                <p className="text-green-400 text-xs font-bold uppercase tracking-wider mb-1">💰 Tips Received</p>
                                <p className="text-white font-bold text-2xl">₹{gig.totalTips.toLocaleString()}</p>
                                <p className="text-gray-400 text-sm">from {gig.tipCount} {gig.tipCount === 1 ? 'person' : 'people'}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ EARNINGS TAB ══ */}
        {tab === 'earnings' && (
          <div className="space-y-6">
            <div className="artist-section-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="artist-section-header mb-0"><span className="emoji-icon">💰</span><span>EARNINGS DASHBOARD</span></h2>
                <button
                  onClick={() => { setEarnings(null); setLoadingEarnings(true); getEarningsData(currentUser.uid).then(setEarnings).finally(() => setLoadingEarnings(false)); }}
                  className="btn btn-ghost text-sm">🔄 Refresh</button>
              </div>

              {loadingEarnings && (
                <div className="text-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-electric mx-auto mb-4"></div>
                  <p className="text-electric font-bold">Loading earnings...</p>
                </div>
              )}

              {!loadingEarnings && earnings && (
                <>
                  {/* ── Summary cards ── */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                    <div className="rounded-xl p-4 text-center col-span-2 md:col-span-1"
                      style={{ background: 'rgba(34,197,94,0.12)', border: '2px solid rgba(34,197,94,0.4)' }}>
                      <p className="text-3xl mb-1">💰</p>
                      <p className="text-3xl font-bold text-white">₹{earnings.summary.grandTotal.toLocaleString()}</p>
                      <p className="text-green-400 font-bold text-sm mt-1">Total Earned</p>
                    </div>
                    <StatCard emoji="🏢" label="From Bookings"
                      value={`₹${earnings.summary.totalFromBookings.toLocaleString()}`}
                      sub={`${earnings.summary.paidGigCount} paid gigs`}
                      color="#00d4ff" />
                    <StatCard emoji="🎁" label="From Tips"
                      value={`₹${earnings.summary.totalTips.toLocaleString()}`}
                      sub={`${earnings.tipsByGig?.length || 0} gigs with tips`}
                      color="#a855f7" />
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-8">
                    <StatCard emoji="📅" label="Upcoming Bookings"
                      value={earnings.summary.upcomingBookingCount}
                      color="#f59e0b" />
                    <StatCard emoji="✅" label="Total Confirmed"
                      value={earnings.summary.confirmedBookingCount}
                      color="#22c55e" />
                  </div>

                  {/* ── Monthly breakdown ── */}
                  {earnings.monthly?.length > 0 && (
                    <div className="mb-8">
                      <p className="text-electric text-xs font-bold uppercase tracking-wider mb-4">📆 Monthly Breakdown</p>
                      <div className="space-y-2">
                        {earnings.monthly.map(m => {
                          const [year, month] = m.month.split('-');
                          const label = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                          const total = m.bookingIncome + m.tips;
                          return (
                            <div key={m.month} className="flex items-center justify-between p-4 rounded-xl"
                              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                              <div>
                                <p className="text-white font-bold">{label}</p>
                                <div className="flex gap-3 mt-1">
                                  {m.bookingIncome > 0 && (
                                    <span className="text-xs text-electric">🏢 ₹{m.bookingIncome.toLocaleString()} ({m.bookingCount} gig{m.bookingCount !== 1 ? 's' : ''})</span>
                                  )}
                                  {m.tips > 0 && (
                                    <span className="text-xs text-purple-400">🎁 ₹{m.tips.toLocaleString()} tips</span>
                                  )}
                                </div>
                              </div>
                              <p className="text-green-400 font-bold text-lg">₹{total.toLocaleString()}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ── Upcoming bookings ── */}
                  {earnings.upcomingBookings?.length > 0 && (
                    <div className="mb-8">
                      <p className="text-electric text-xs font-bold uppercase tracking-wider mb-4">📅 Upcoming Paid Gigs</p>
                      <div className="space-y-3">
                        {earnings.upcomingBookings.map(b => (
                          <div key={b.id} className="flex items-center justify-between p-4 rounded-xl"
                            style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)' }}>
                            <div>
                              <p className="text-white font-bold">🏢 {b.venueName}</p>
                              <p className="text-gray-400 text-sm">📅 {b.eventDate}{b.eventTime ? ` at ${b.eventTime}` : ''}</p>
                              {b.duration && <p className="text-gray-500 text-xs">⏱️ {b.duration} hours</p>}
                            </div>
                            <div className="text-right">
                              <p className="text-green-400 font-bold text-xl">₹{Number(b.agreedBudget).toLocaleString()}</p>
                              <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                                style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>
                                ✅ Confirmed
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Tips by gig ── */}
                  {earnings.tipsByGig?.length > 0 && (
                    <div className="mb-6">
                      <p className="text-electric text-xs font-bold uppercase tracking-wider mb-4">🎁 Tips by Gig</p>
                      <div className="space-y-3">
                        {earnings.tipsByGig.map(t => (
                          <div key={t.gigId} className="flex items-center justify-between p-4 rounded-xl"
                            style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)' }}>
                            <div>
                              <p className="text-white font-bold">{t.venueName}</p>
                              <p className="text-gray-400 text-sm">📅 {t.gigDate}</p>
                              <p className="text-gray-500 text-xs">{t.tipCount} tip{t.tipCount !== 1 ? 's' : ''}</p>
                            </div>
                            <p className="text-purple-400 font-bold text-xl">₹{t.tipTotal.toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {earnings.summary.grandTotal === 0 && (
                    <div className="artist-empty-state">
                      <p className="text-4xl mb-3">💰</p>
                      <p className="artist-empty-text">No earnings yet. Confirmed bookings and tips will appear here!</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* ══ HIRE REQUESTS TAB ══ */}
        {tab === 'hire' && (
          <div className="space-y-6">
            <div className="artist-section-card">
              <h2 className="artist-section-header"><span className="emoji-icon">🏢</span><span>HIRE REQUESTS FROM VENUES</span></h2>
              {hireRequests.length === 0 ? (
                <div className="artist-empty-state"><p className="artist-empty-text">No hire requests yet. Venues will reach out here! 🏢</p></div>
              ) : (
                <div className="space-y-4">
                  {hireRequests.map(req => (
                    <div key={req.id} className="artist-song-card p-6">
                      <div className="flex flex-col md:flex-row md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">🏢 {req.venueName}</h3>
                            <span className="text-xs font-bold px-2 py-1 rounded-full"
                              style={{ background: `${hireStatusColor[req.status]}22`, color: hireStatusColor[req.status], border: `1px solid ${hireStatusColor[req.status]}55` }}>
                              {hireStatusLabel[req.status]}
                            </span>
                          </div>
                          <p className="text-electric font-bold">📅 {req.eventDate} at {req.eventTime}</p>
                          <p className="text-gray-300 text-sm">⏱️ {req.duration}h &nbsp;·&nbsp; 💰 Offer: ₹{req.currentBudget?.toLocaleString()}</p>
                          {req.venueLocation && <p className="text-gray-400 text-sm">📍 {req.venueLocation}</p>}
                          {req.description && <p className="text-gray-400 text-sm mt-1">{req.description}</p>}
                        </div>
                        <button onClick={() => setExpandedHireReq(expandedHireReq === req.id ? null : req.id)}
                          className="btn btn-ghost text-sm self-start">
                          {expandedHireReq === req.id ? '▲ Hide' : '▼ History'}
                        </button>
                      </div>
                      {expandedHireReq === req.id && (
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
                          <p className="text-white font-bold text-sm mb-3">🕐 Negotiation History</p>
                          {(req.negotiationHistory || []).map((entry, i) => (
                            <div key={i} className={`p-3 rounded-lg text-sm ${entry.by === 'artist' ? 'mr-8' : 'ml-8'}`}
                              style={{ background: entry.by === 'venue' ? 'rgba(59,130,246,0.12)' : 'rgba(168,85,247,0.12)', border: `1px solid ${entry.by === 'venue' ? 'rgba(59,130,246,0.3)' : 'rgba(168,85,247,0.3)'}` }}>
                              <div className="flex justify-between mb-1">
                                <span className="font-bold" style={{ color: entry.by === 'venue' ? '#60a5fa' : '#c084fc' }}>
                                  {entry.by === 'venue' ? '🏢 Venue' : '🎸 You'} · {entry.action}
                                </span>
                                <span className="text-gray-400 text-xs">{entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : ''}</span>
                              </div>
                              <p className="text-white font-semibold">💰 ₹{entry.budget?.toLocaleString()}</p>
                              {entry.message && <p className="text-gray-300 mt-1">{entry.message}</p>}
                            </div>
                          ))}
                          {req.status === 'pending' && (
                            <div className="mt-3 p-4 rounded-xl" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)' }}>
                              <p className="text-blue-400 font-bold text-sm mb-3">💬 Respond to this offer:</p>
                              {respondingTo === req.id ? (
                                <div className="space-y-3">
                                  <input type="number" value={counterForm.budget}
                                    onChange={e => setCounterForm({ ...counterForm, budget: e.target.value })}
                                    placeholder="Your counter budget (₹)"
                                    className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm" />
                                  <textarea value={counterForm.message}
                                    onChange={e => setCounterForm({ ...counterForm, message: e.target.value })}
                                    placeholder="Message to venue (optional)" rows={2}
                                    className="w-full px-3 py-2 rounded-lg bg-white/10 text-white border border-white/20 focus:border-electric focus:outline-none text-sm resize-none" />
                                  <div className="flex flex-wrap gap-2">
                                    <button onClick={() => handleArtistRespond(req.id, 'accept')} className="btn btn-electric text-sm">✅ Accept</button>
                                    <button onClick={() => handleArtistRespond(req.id, 'counter')} className="btn btn-neon text-sm">🔄 Counter</button>
                                    <button onClick={() => handleArtistRespond(req.id, 'decline')} className="btn btn-fire text-sm">❌ Decline</button>
                                    <button onClick={() => setRespondingTo(null)} className="btn btn-ghost text-sm">Cancel</button>
                                  </div>
                                </div>
                              ) : (
                                <button onClick={() => setRespondingTo(req.id)} className="btn btn-electric text-sm">💬 Respond</button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {artistBookings.length > 0 && (
                <div className="mt-8">
                  <h3 className="artist-section-header"><span className="emoji-icon">📅</span><span>MY BOOKINGS</span></h3>
                  <div className="space-y-3">
                    {artistBookings.map(booking => (
                      <div key={booking.id} className="artist-song-card p-5">
                        <h4 className="text-lg font-bold text-white">🏢 {booking.venueName}</h4>
                        <p className="text-electric font-bold">📅 {booking.eventDate} at {booking.eventTime}</p>
                        <p className="text-gray-300 text-sm">⏱️ {booking.duration} hours &nbsp;·&nbsp; 💰 ₹{booking.agreedBudget?.toLocaleString()} agreed</p>
                        <span className="inline-block mt-2 text-xs font-bold px-3 py-1 rounded-full"
                          style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.4)' }}>✅ Confirmed</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══ AVAILABILITY CALENDAR TAB ══ */}
        {tab === 'avail_cal' && (() => {
          const year = availCalDate.getFullYear();
          const month = availCalDate.getMonth();
          const firstDay = new Date(year, month, 1).getDay();
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const today = new Date();
          const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
          const monthLabel = availCalDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

          // Build gig date set for this month
          const gigDates = {};
          gigs.forEach(g => {
            const d = g.gigDate || g.date;
            if (d) {
              const status = calculateGigStatus(g);
              if (status !== 'ended' && status !== 'cancelled') gigDates[d] = g;
            }
          });

          const toggleBlocked = async (dateStr) => {
            // Can't block a gig date
            if (gigDates[dateStr]) { alert('⚠️ You have a gig on this date. Remove the gig first to block it.'); return; }
            // Can't block past dates
            if (dateStr < todayStr) return;
            const newBlocked = blockedDates.includes(dateStr)
              ? blockedDates.filter(d => d !== dateStr)
              : [...blockedDates, dateStr];
            setBlockedDates(newBlocked);
            setSavingBlocked(true);
            try {
              await saveBlockedDates(currentUser.uid, newBlocked);
            } catch (err) { alert('Error saving: ' + err.message); }
            finally { setSavingBlocked(false); }
          };

          const cells = [];
          for (let i = 0; i < firstDay; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);

          return (
            <div className="space-y-6">
              <div className="artist-section-card">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="artist-section-header mb-0"><span className="emoji-icon">🗓️</span><span>AVAILABILITY CALENDAR</span></h2>
                  {savingBlocked && <span className="text-electric text-xs font-bold animate-pulse">Saving...</span>}
                </div>
                <p className="text-gray-400 text-sm mb-6">
                  Click any future date to mark it as <span className="text-red-400 font-bold">blocked</span> (unavailable). Your gig dates are shown automatically.
                </p>

                {/* Month nav */}
                <div className="flex items-center justify-between mb-5">
                  <button onClick={() => setAvailCalDate(new Date(year, month - 1, 1))} className="btn btn-ghost text-sm px-3 py-2">← Prev</button>
                  <h3 className="text-white font-bold text-lg">{monthLabel}</h3>
                  <button onClick={() => setAvailCalDate(new Date(year, month + 1, 1))} className="btn btn-ghost text-sm px-3 py-2">Next →</button>
                </div>

                {/* Day headers */}
                <div className="grid grid-cols-7 mb-2">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                    <div key={d} className="text-center text-xs font-bold text-gray-500 py-1">{d}</div>
                  ))}
                </div>

                {/* Day cells */}
                <div className="grid grid-cols-7 gap-1">
                  {cells.map((day, idx) => {
                    if (!day) return <div key={`e-${idx}`} />;
                    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const isToday = dateStr === todayStr;
                    const isPast = dateStr < todayStr;
                    const hasGig = !!gigDates[dateStr];
                    const isBlocked = blockedDates.includes(dateStr);

                    let bg, border, textColor, cursor;
                    if (hasGig) {
                      bg = 'rgba(0,212,255,0.15)'; border = '1px solid rgba(0,212,255,0.5)';
                      textColor = '#00d4ff'; cursor = 'default';
                    } else if (isBlocked) {
                      bg = 'rgba(239,68,68,0.18)'; border = '1px solid rgba(239,68,68,0.5)';
                      textColor = '#f87171'; cursor = 'pointer';
                    } else if (isPast) {
                      bg = 'rgba(255,255,255,0.02)'; border = '1px solid rgba(255,255,255,0.05)';
                      textColor = '#374151'; cursor = 'default';
                    } else if (isToday) {
                      bg = 'rgba(0,212,255,0.08)'; border = '1px solid rgba(0,212,255,0.5)';
                      textColor = '#ffffff'; cursor = 'pointer';
                    } else {
                      bg = 'rgba(255,255,255,0.04)'; border = '1px solid rgba(255,255,255,0.08)';
                      textColor = '#d1d5db'; cursor = 'pointer';
                    }

                    return (
                      <div key={dateStr}
                        onClick={() => !isPast && !hasGig && toggleBlocked(dateStr)}
                        className="rounded-lg p-1 min-h-[52px] flex flex-col items-center transition-all hover:opacity-80"
                        style={{ background: bg, border, cursor }}>
                        <span className="text-xs font-bold mt-0.5" style={{ color: textColor }}>{day}</span>
                        {hasGig && (
                          <span className="text-[8px] font-bold mt-0.5 text-center leading-tight"
                            style={{ color: '#00d4ff' }}>GIG</span>
                        )}
                        {isBlocked && !hasGig && (
                          <span className="text-[9px] font-bold mt-0.5" style={{ color: '#f87171' }}>✕</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.5)' }}></div>
                    <span className="text-xs text-gray-400">Gig scheduled</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.5)' }}></div>
                    <span className="text-xs text-gray-400">Blocked (unavailable)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}></div>
                    <span className="text-xs text-gray-400">Available — click to block</span>
                  </div>
                </div>

                {/* Blocked dates list */}
                {blockedDates.filter(d => d >= todayStr).length > 0 && (
                  <div className="mt-6">
                    <p className="text-electric text-xs font-bold uppercase tracking-wider mb-3">
                      🚫 Blocked Dates ({blockedDates.filter(d => d >= todayStr).length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {blockedDates
                        .filter(d => d >= todayStr)
                        .sort()
                        .map(d => (
                          <div key={d}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                            style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)' }}>
                            <span className="text-red-400 text-sm font-bold">
                              {new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            <button
                              onClick={async () => {
                                const newBlocked = blockedDates.filter(x => x !== d);
                                setBlockedDates(newBlocked);
                                setSavingBlocked(true);
                                try { await saveBlockedDates(currentUser.uid, newBlocked); }
                                catch (err) { alert('Error: ' + err.message); }
                                finally { setSavingBlocked(false); }
                              }}
                              className="text-red-400 hover:text-white text-xs font-bold">✕</button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {blockedDates.filter(d => d >= todayStr).length === 0 && (
                  <div className="mt-6 text-center py-4 rounded-xl"
                    style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)' }}>
                    <p className="text-green-400 font-bold text-sm">✅ All future dates available</p>
                    <p className="text-gray-500 text-xs mt-1">Click any future date on the calendar to mark it as blocked.</p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

      </div>

      {/* Playlist Edit Modal */}
      {showPlaylistModal && editingPlaylist && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="gig-card border-2 border-electric w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
              <h2 className="concert-heading text-xl text-electric flex items-center gap-2">🎵 EDIT GIG PLAYLIST</h2>
              <button onClick={() => { setShowPlaylistModal(false); setSetlistSearch(''); }} className="text-white hover:text-neon text-2xl">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <input type="text" value={editingPlaylist.name}
                onChange={e => setEditingPlaylist({ ...editingPlaylist, name: e.target.value })}
                placeholder="New Playlist"
                className="w-full px-4 py-3 rounded-lg bg-white/10 text-white placeholder-white/40 border border-white/20 focus:border-electric focus:outline-none text-lg font-bold" />
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
                <input type="text" value={setlistSearch} onChange={e => setSetlistSearch(e.target.value)}
                  placeholder="Search songs..."
                  className="w-full pl-9 pr-4 py-2 rounded-lg bg-white/10 text-white placeholder-white/40 border border-white/20 focus:border-electric focus:outline-none text-sm" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-magenta text-sm font-bold">📋 ADD FROM MASTER</span>
                  <span className="flex items-center gap-1 px-3 py-1 bg-electric/20 border border-electric/40 text-electric text-xs font-bold rounded-lg">🎸 {editingPlaylist.songs.length}</span>
                </div>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(() => {
                    const searchTerm = setlistSearch.trim().toLowerCase();
                    const notInSetlist = masterSongs.filter(s => !editingPlaylist.songs.includes(s.id));
                    const filtered = notInSetlist.filter(s => !searchTerm || s.title.toLowerCase().includes(searchTerm) || s.artist.toLowerCase().includes(searchTerm));
                    if (masterSongs.length === 0) return (
                      <div className="text-center py-5 rounded-lg border border-dashed border-white/10">
                        <p className="text-2xl mb-1">🎸</p>
                        <p className="text-white font-bold text-sm">No songs in Master Playlist</p>
                        <p className="text-gray-400 text-xs mt-1">Go to the <span className="text-electric font-semibold">Master Playlist</span> tab and add songs first.</p>
                      </div>
                    );
                    if (notInSetlist.length === 0) return (
                      <div className="text-center py-4 rounded-lg" style={{ background: 'rgba(57,255,20,0.05)', border: '1px solid rgba(57,255,20,0.15)' }}>
                        <p className="text-neon font-bold text-sm">✅ All songs added to setlist!</p>
                      </div>
                    );
                    if (filtered.length === 0 && searchTerm) {
                      const alreadyInSetlist = masterSongs.find(s => editingPlaylist.songs.includes(s.id) && (s.title.toLowerCase().includes(searchTerm) || s.artist.toLowerCase().includes(searchTerm)));
                      return (
                        <div className="text-center py-5 rounded-lg border border-dashed border-orange-500/20" style={{ background: 'rgba(249,115,22,0.05)' }}>
                          <p className="text-2xl mb-1">{alreadyInSetlist ? '✅' : '🔍'}</p>
                          {alreadyInSetlist ? (
                            <p className="text-orange-300 font-bold text-sm">"{alreadyInSetlist.title}" is already in your setlist</p>
                          ) : (
                            <>
                              <p className="text-orange-300 font-bold text-sm">"{setlistSearch}" not found in Master Playlist</p>
                              <p className="text-gray-400 text-xs mt-1">Go to <span className="text-electric font-semibold">Master Playlist</span> tab → Add Song to add it first.</p>
                            </>
                          )}
                        </div>
                      );
                    }
                    return filtered.map(song => (
                      <div key={song.id} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg hover:border-electric/40 transition">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-bold truncate">{song.title}</p>
                          <p className="text-gray-400 text-xs truncate">{song.artist}</p>
                        </div>
                        <button onClick={() => setEditingPlaylist({ ...editingPlaylist, songs: [...editingPlaylist.songs, song.id] })}
                          className="flex-shrink-0 px-3 py-1 bg-green-600/80 hover:bg-green-500 text-white text-xs font-bold rounded-lg">+ Add</button>
                      </div>
                    ));
                  })()}
                </div>
              </div>
              <div>
                <p className="text-electric text-sm font-bold mb-2">🎵 CURRENT SETLIST ({editingPlaylist.songs.length})</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {editingPlaylist.songs.map((songId, idx) => {
                    const song = masterSongs.find(s => s.id === songId);
                    if (!song) return null;
                    return (
                      <div key={songId} className="flex items-center gap-2 px-3 py-2 bg-electric/10 border border-electric/30 rounded-lg">
                        <span className="text-electric text-xs font-bold w-5">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-bold truncate">{song.title}</p>
                          <p className="text-gray-400 text-xs truncate">{song.artist}</p>
                        </div>
                        <button onClick={() => setEditingPlaylist({ ...editingPlaylist, songs: editingPlaylist.songs.filter(id => id !== songId) })}
                          className="text-red-400 hover:text-red-300 text-lg">✕</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-5 py-4 border-t border-white/10 flex-shrink-0">
              <button onClick={() => { saveGigPlaylist(); setSetlistSearch(''); }} className="flex-1 btn btn-electric">✓ Save Setlist</button>
              <button onClick={() => { setShowPlaylistModal(false); setSetlistSearch(''); }} className="btn btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showProfileSetup && (
        <div className="modal-overlay" onClick={() => setShowProfileSetup(false)}>
          <div className="modal-content" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">🎸 Edit Profile</h2>
              <button className="modal-close" onClick={() => setShowProfileSetup(false)}>✕</button>
            </div>
            <div className="modal-body space-y-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <p className="text-electric text-xs font-bold uppercase tracking-wider">Basic Info</p>
              {[
                { label: 'Artist Name *', key: 'artistName', placeholder: 'Your artist/band name' },
                { label: 'Full Name *', key: 'fullName', placeholder: 'Your full name' },
                { label: 'Genre *', key: 'genre', placeholder: 'e.g. Rock, Jazz, Pop' },
                { label: 'Location *', key: 'location', placeholder: 'Your city' },
              ].map(({ label, key, placeholder }) => (
                <div key={key} className="form-group">
                  <label>{label}</label>
                  <input type="text" value={profileData[key] || ''} onChange={e => setProfileData({ ...profileData, [key]: e.target.value })} placeholder={placeholder} />
                </div>
              ))}
              <div className="form-group">
                <label>Bio * (min 50 chars)</label>
                <textarea value={profileData.bio || ''} onChange={e => setProfileData({ ...profileData, bio: e.target.value })} placeholder="Tell the audience about yourself..." rows={3} />
                <p className="text-gray-light text-xs mt-1">{(profileData.bio || '').length}/50 minimum</p>
              </div>
              <p className="text-electric text-xs font-bold uppercase tracking-wider pt-2">💰 Pricing (₹)</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="form-group">
                  <label>Min Price (₹)</label>
                  <input type="number" value={profileData.priceMin || ''} onChange={e => setProfileData({ ...profileData, priceMin: e.target.value })} placeholder="e.g. 3000" />
                </div>
                <div className="form-group">
                  <label>Max Price (₹)</label>
                  <input type="number" value={profileData.priceMax || ''} onChange={e => setProfileData({ ...profileData, priceMax: e.target.value })} placeholder="e.g. 15000" />
                </div>
              </div>
              <p className="text-electric text-xs font-bold uppercase tracking-wider pt-2">📅 Availability</p>
              <div className="flex items-center gap-3">
                <button onClick={() => setProfileData({ ...profileData, isAvailable: !profileData.isAvailable })}
                  className={`px-4 py-2 rounded-lg font-bold text-sm transition-all ${profileData.isAvailable ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-red-500/20 text-red-400 border border-red-500/50'}`}>
                  {profileData.isAvailable ? '✅ Available for Hire' : '❌ Not Available'}
                </button>
                <p className="text-gray-400 text-xs">Click to toggle</p>
              </div>
              <div className="form-group">
                <label>Availability Note</label>
                <input type="text" value={profileData.availabilityNote || ''} onChange={e => setProfileData({ ...profileData, availabilityNote: e.target.value })} placeholder="e.g. Weekends only, 2 weeks notice required" />
              </div>
              <p className="text-electric text-xs font-bold uppercase tracking-wider pt-2">🔗 Social Media</p>
              {[
                { key: 'instagram', label: 'Instagram', placeholder: '@handle or full URL' },
                { key: 'youtube', label: 'YouTube', placeholder: 'Channel URL' },
                { key: 'spotify', label: 'Spotify', placeholder: 'Artist profile URL' },
                { key: 'facebook', label: 'Facebook', placeholder: 'Page URL' },
              ].map(({ key, label, placeholder }) => (
                <div key={key} className="form-group">
                  <label>{label}</label>
                  <input type="text" value={profileData.socialMedia?.[key] || ''}
                    onChange={e => setProfileData({ ...profileData, socialMedia: { ...profileData.socialMedia, [key]: e.target.value } })}
                    placeholder={placeholder} />
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowProfileSetup(false)} className="btn btn-ghost">Cancel</button>
              <button onClick={handleSaveProfile} className="btn btn-electric">Save Profile</button>
            </div>
          </div>
        </div>
      )}

      {/* Follower Count Modal */}
      {showFollowerModal && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="gig-card w-full max-w-lg">
            <div className="px-6 py-4 border-b border-white/10">
              {followerModalMode === 'reminder' ? (
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">📊 Update Your Follower Counts</h2>
                  <p className="text-gray-400 text-sm">
                    {artistProfile?.followerCountsUpdatedAt
                      ? "It's been over 30 days since you last updated your follower counts."
                      : "You haven't added your follower counts yet. Adding them helps venues find and hire you!"}
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-white">✏️ Update Follower Counts</h2>
                  <button onClick={() => setShowFollowerModal(false)} className="text-white hover:text-neon text-2xl">✕</button>
                </div>
              )}
            </div>
            <div className="px-6 py-5 space-y-3">
              <p className="text-gray-400 text-xs mb-4">Enter your current follower / subscriber counts. Leave blank if you don't have that platform.</p>
              {[
                { key: 'instagram', label: 'Instagram Followers', icon: '📸', color: '#E1306C', placeholder: 'e.g. 4500' },
                { key: 'youtube', label: 'YouTube Subscribers', icon: '▶️', color: '#FF0000', placeholder: 'e.g. 12000' },
                { key: 'spotify', label: 'Spotify Followers', icon: '🎵', color: '#1DB954', placeholder: 'e.g. 800' },
                { key: 'facebook', label: 'Facebook Followers', icon: '👍', color: '#1877F2', placeholder: 'e.g. 3200' },
                { key: 'tiktok', label: 'TikTok Followers', icon: '🎵', color: '#ee1d52', placeholder: 'e.g. 9000' },
                { key: 'twitter', label: 'Twitter / X Followers', icon: '🐦', color: '#1DA1F2', placeholder: 'e.g. 1500' },
                { key: 'soundcloud', label: 'SoundCloud Followers', icon: '☁️', color: '#FF5500', placeholder: 'e.g. 600' },
              ].map(({ key, label, icon, color, placeholder }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-lg w-7 text-center flex-shrink-0">{icon}</span>
                  <div className="flex-1">
                    <label className="text-xs font-bold mb-1 block" style={{ color }}>{label}</label>
                    <input type="number" min="0" value={followerCounts[key] || ''}
                      onChange={e => setFollowerCounts(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder={placeholder}
                      className="w-full px-3 py-2 rounded-lg text-white text-sm focus:outline-none"
                      style={{ background: 'rgba(255,255,255,0.07)', border: `1px solid ${color}40` }} />
                  </div>
                </div>
              ))}
              <p className="text-yellow-500/70 text-xs pt-2">⚠️ Please enter accurate counts. Inflated numbers may result in your data being hidden by an admin.</p>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-white/10">
              <button onClick={handleSaveFollowerCounts} disabled={savingFollowers} className="flex-1 btn btn-electric">
                {savingFollowers ? '⏳ Saving...' : '✅ Save Counts'}
              </button>
              {followerModalMode === 'reminder' && (
                <button onClick={handleSnoozeFollower} className="btn btn-ghost text-sm">⏰ Remind Later</button>
              )}
              {followerModalMode === 'edit' && (
                <button onClick={() => setShowFollowerModal(false)} className="btn btn-ghost">Cancel</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
